import { createEnv } from "@t3-oss/env-core";
import { z } from "zod";

export const env = createEnv({
	server: {
		DATABASE_URL: z.url("DATABASE_URL must be a valid database URL"),
		SERVER_URL: z.url().optional(),
		BETTER_AUTH_SECRET: z.string().min(1).optional(),
		BETTER_AUTH_URL: z.url().optional(),
		SEED_ADMIN_NAME: z.string().min(1).optional(),
		SEED_ADMIN_EMAIL: z.email().optional(),
		SEED_ADMIN_PASSWORD: z.string().min(8).optional(),

		// S3-compatible object storage (MinIO) for training assets uploaded from
		// the admin — see src/lib/storage.ts. All optional as a group: with any
		// of them missing the upload route answers 503 and the admin falls back
		// to typing a path, so a dev machine with no MinIO still runs.
		S3_ENDPOINT: z.url().optional(),
		S3_REGION: z.string().min(1).default("us-east-1"),
		S3_BUCKET: z.string().min(1).optional(),
		S3_ACCESS_KEY: z.string().min(1).optional(),
		S3_SECRET_KEY: z.string().min(1).optional(),
		// Only needed when browsers reach MinIO on a different hostname than the
		// app does (private network in, public hostname or CDN out).
		S3_PUBLIC_URL: z.url().optional(),
	},

	/**
	 * The prefix that client-side variables must have. This is enforced both at
	 * a type-level and at runtime.
	 */
	clientPrefix: "VITE_",

	client: {
		VITE_APP_TITLE: z.string().min(1).optional(),
		VITE_GA_MEASUREMENT_ID: z.string().min(1).optional(),
		// Logs every analytics event to the console — turn on while checking the
		// pixel with the Meta Pixel Helper, off in production.
		VITE_ANALYTICS_DEBUG: z.enum(["true", "false"]).default("false"),
		// Re-inits the pixel with the lead's hashed email/phone (Meta Advanced
		// Matching) so conversions attribute back to the person who saw the ad.
		VITE_META_ADVANCED_MATCHING: z.enum(["true", "false"]).default("false"),
		// Monetary value of one lead, in DZD. Sent with Lead/CompleteRegistration
		// so Meta can report and optimise on ROAS. Omit to send no value.
		VITE_LEAD_VALUE: z.coerce.number().positive().optional(),
	},

	/**
	 * What object holds the environment variables at runtime. This is usually
	 * `process.env` or `import.meta.env`.
	 */
	runtimeEnv: {
		...import.meta.env,
		...process.env,
	},

	/**
	 * By default, this library will feed the environment variables directly to
	 * the Zod validator.
	 *
	 * This means that if you have an empty string for a value that is supposed
	 * to be a number (e.g. `PORT=` in a ".env" file), Zod will incorrectly flag
	 * it as a type mismatch violation. Additionally, if you have an empty string
	 * for a value that is supposed to be a string with a default value (e.g.
	 * `DOMAIN=` in an ".env" file), the default value will never be applied.
	 *
	 * In order to solve these issues, we recommend that all new projects
	 * explicitly specify this option as true.
	 */
	emptyStringAsUndefined: true,
});
